import { useState, useEffect } from 'react';
import { ArrowRight, Clock, DollarSign, Heart, Users, ChevronRight, Star, Check, Briefcase, Wrench, BookOpen, MessageSquare, Palette, Fish } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Landing = () => {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [waitlistCount, setWaitlistCount] = useState(0);

  useEffect(() => {
    fetch(`${API_URL}/api/waitlist/count`)
      .then(res => res.json())
      .then(data => { if (data.success) setWaitlistCount(data.count); })
      .catch(() => {});
  }, []);
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (data.success) {
        setSubmitted(true);
        setWaitlistCount(prev => prev + 1);
      } else {
        setError(data.detail || 'Something went wrong. Please try again.');
      }
    } catch {
      setError('Could not connect. Please try again later.');
    }
    setLoading(false);
  };

  const scrollToSignup = () => {
    document.getElementById('signup-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-mate-sand font-body text-gray-900 overflow-x-hidden" data-testid="landing-page">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-mate-sand/90 backdrop-blur-md border-b border-mate-mist/50" data-testid="landing-nav">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16 md:h-20">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-mate-green flex items-center justify-center">
              <span className="text-white font-heading font-bold text-lg">6</span>
            </div>
            <span className="font-heading font-bold text-xl text-mate-green tracking-tight">6th Mate</span>
          </div>
          <button
            onClick={scrollToSignup}
            className="hidden sm:flex items-center gap-2 bg-mate-terracotta text-white font-semibold text-sm px-6 py-2.5 rounded-full hover:scale-105 transition-transform duration-200"
            data-testid="nav-cta-btn"
            aria-label="Join the early mentor list"
          >
            Join Early List
            <ArrowRight size={16} />
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-28 md:pt-36 pb-16 md:pb-24 px-6 md:px-12" data-testid="hero-section">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12 md:gap-16 items-center">
          <div className="order-2 md:order-1">
            <div className="inline-flex items-center gap-2 bg-mate-green/10 text-mate-green px-4 py-2 rounded-full text-sm font-semibold mb-6">
              <span className="w-2 h-2 rounded-full bg-mate-terracotta animate-pulse" />
              Australian-based &middot; Launching soon
            </div>
            <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl text-gray-900 leading-[1.1] tracking-tight mb-6" style={{ letterSpacing: '-0.02em' }}>
              Turn your lifetime of wisdom into{' '}
              <span className="text-mate-terracotta">flexible income</span>
            </h1>
            <p className="text-lg md:text-xl text-gray-600 leading-relaxed mb-8 max-w-lg" style={{ lineHeight: '1.65' }}>
              Share advice from your favourite chair. No commute. No boss. Just relaxed video calls with people who truly value what you know.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={scrollToSignup}
                className="flex items-center justify-center gap-2 bg-mate-terracotta text-white font-semibold text-lg px-8 py-4 rounded-full hover:scale-105 transition-transform duration-200 shadow-lg shadow-mate-terracotta/20"
                data-testid="hero-cta-btn"
                aria-label="Join the early mentor list"
              >
                Join the Early Mentor List
                <ArrowRight size={20} />
              </button>
              <button
                onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })}
                className="flex items-center justify-center gap-2 text-mate-green font-semibold text-lg px-8 py-4 rounded-full border-2 border-mate-green/20 hover:border-mate-green/40 transition-colors duration-200"
                data-testid="hero-learn-btn"
                aria-label="Learn how it works"
              >
                How It Works
                <ChevronRight size={20} />
              </button>
            </div>
          </div>

          {/* Hero Image */}
          <div className="order-1 md:order-2 flex justify-center">
            <div className="relative">
              <div className="w-72 h-[500px] md:w-80 md:h-[560px] rounded-[2.5rem] overflow-hidden shadow-2xl border-[6px] border-gray-900 relative z-10">
                <img
                  src="/screenshots/app_mike.png"
                  alt="6th Mate app - mentor sharing expertise"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              {/* Floating accent card */}
              <div className="absolute -bottom-4 -left-8 bg-white rounded-2xl shadow-xl p-4 z-20 animate-float" style={{ animationDelay: '0.5s' }}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <DollarSign size={20} className="text-mate-green" />
                  </div>
                  <div>
                    <p className="font-semibold text-sm text-gray-900">$85 earned</p>
                    <p className="text-xs text-gray-500">30 min session</p>
                  </div>
                </div>
              </div>
              {/* Background shape */}
              <div className="absolute -top-8 -right-8 w-full h-full bg-mate-green/10 rounded-[3rem] -z-10" />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 md:py-32 px-6 md:px-12 bg-white" data-testid="benefits-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-gray-900 mb-4" style={{ letterSpacing: '-0.02em' }}>
              Why Retirees Love 6th Mate
            </h2>
            <p className="text-lg text-gray-500 max-w-2xl mx-auto">
              You've earned every bit of your experience — why not get paid to pass it on?
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {[
              {
                icon: <Clock size={28} />,
                title: 'Flexible & Low-Effort',
                points: [
                  'Set your own rates, hours, and availability',
                  'Do sessions from the beach, garden, or armchair',
                  '15, 30, or 60 minutes — whatever suits you'
                ]
              },
              {
                icon: <DollarSign size={28} />,
                title: 'Meaningful Income',
                points: [
                  'Charge what your expertise is worth ($5-$150+/hr)',
                  'Platform takes only a small service fee',
                  'Cash out anytime, like Uber'
                ]
              },
              {
                icon: <Heart size={28} />,
                title: 'Purpose & Connection',
                points: [
                  'Help the next generation navigate life',
                  'Stay sharp, valued, and socially active',
                  'Careers, hobbies, DIY, relationships & more'
                ]
              },
              {
                icon: <Star size={28} />,
                title: 'Perfect Timing',
                points: [
                  'Young people are hungry for real-world guidance',
                  'Confirm if a path is worth pursuing',
                  'Be the wise uncle everyone wishes they had'
                ]
              }
            ].map((benefit, i) => (
              <div
                key={i}
                className="group bg-mate-sand rounded-2xl p-6 md:p-8 border border-mate-mist hover:border-mate-terracotta/40 transition-all duration-300 hover:-translate-y-1"
                data-testid={`benefit-card-${i}`}
              >
                <div className="w-14 h-14 rounded-2xl bg-mate-terracotta/10 flex items-center justify-center text-mate-terracotta mb-5 group-hover:bg-mate-terracotta group-hover:text-white transition-colors duration-300">
                  {benefit.icon}
                </div>
                <h3 className="font-heading font-semibold text-xl text-gray-900 mb-4">{benefit.title}</h3>
                <ul className="space-y-2.5">
                  {benefit.points.map((point, j) => (
                    <li key={j} className="flex items-start gap-2 text-gray-600 text-base" style={{ lineHeight: '1.6' }}>
                      <Check size={16} className="text-mate-green mt-1 shrink-0" />
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20 md:py-32 px-6 md:px-12 bg-mate-sand" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-gray-900 mb-4" style={{ letterSpacing: '-0.02em' }}>
              How It Works
            </h2>
            <p className="text-lg text-gray-500">Super simple for you — start earning in 4 easy steps</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 md:gap-12">
            {[
              {
                step: '01',
                title: 'Sign Up & Build Your Profile',
                desc: 'Add a short intro sharing who you are and what you can teach. No tech skills needed — many retirees start with just a photo and bio.',
                color: 'bg-mate-green'
              },
              {
                step: '02',
                title: 'Set Your Availability & Rates',
                desc: 'Block out times that work for you. Pick your session length and price. You stay in full control.',
                color: 'bg-mate-terracotta'
              },
              {
                step: '03',
                title: 'Get Booked',
                desc: 'Motivated seekers browse profiles and request a slot. You review and confirm. No pressure — decline anytime.',
                color: 'bg-mate-gold'
              },
              {
                step: '04',
                title: 'Connect & Earn',
                desc: 'Hop on a simple video call. Share your insights. Get paid automatically after each session. Zero hassle.',
                color: 'bg-mate-green'
              }
            ].map((item, i) => (
              <div
                key={i}
                className="flex gap-6 items-start bg-white rounded-2xl p-6 md:p-8 border border-mate-mist hover:shadow-lg transition-shadow duration-300"
                data-testid={`step-card-${i}`}
              >
                <div className={`${item.color} text-white font-heading font-bold text-2xl w-16 h-16 rounded-2xl flex items-center justify-center shrink-0`}>
                  {item.step}
                </div>
                <div>
                  <h3 className="font-heading font-semibold text-xl text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600 text-base leading-relaxed" style={{ lineHeight: '1.65' }}>{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* App Showcase Section */}
      <section className="py-20 md:py-32 px-6 md:px-12 bg-mate-green overflow-hidden" data-testid="app-showcase-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-white mb-4" style={{ letterSpacing: '-0.02em' }}>
              Your Wisdom, One Tap Away
            </h2>
            <p className="text-lg text-white/70 max-w-xl mx-auto">
              A beautiful, simple app designed for you. Browse mentors, book sessions, and earn — all in one place.
            </p>
          </div>

          <div className="flex justify-center items-end gap-4 md:gap-8 relative">
            {/* Left phone - Discovery */}
            <div className="hidden md:block w-56 h-[400px] rounded-[2rem] overflow-hidden border-[5px] border-white/20 shadow-2xl opacity-80 transform -rotate-6 translate-y-8">
              <img
                src="/screenshots/app_discovery.png"
                alt="6th Mate discovery page"
                className="w-full h-full object-cover object-top"
              />
            </div>

            {/* Center phone - Feed (main) */}
            <div className="w-64 h-[460px] md:w-72 md:h-[520px] rounded-[2.5rem] overflow-hidden border-[6px] border-white/30 shadow-2xl z-10 relative animate-float" style={{ animationDuration: '8s' }}>
              <img
                src="/screenshots/app_feed.png"
                alt="6th Mate main feed"
                className="w-full h-full object-cover object-top"
              />
            </div>

            {/* Right phone - Profile */}
            <div className="hidden md:block w-56 h-[400px] rounded-[2rem] overflow-hidden border-[5px] border-white/20 shadow-2xl opacity-80 transform rotate-6 translate-y-8">
              <img
                src="/screenshots/app_profile.png"
                alt="6th Mate profile page"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* What You Could Teach */}
      <section className="py-20 md:py-32 px-6 md:px-12 bg-white" data-testid="teach-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 md:gap-16 items-center">
            <div>
              <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-gray-900 mb-6" style={{ letterSpacing: '-0.02em' }}>
                What You Could Teach
              </h2>
              <p className="text-lg text-gray-500 mb-8 leading-relaxed">
                If you've spent years getting good at something — anything — someone younger wants to learn it from you.
              </p>
              <div className="space-y-4">
                {[
                  { icon: <Briefcase size={22} />, text: 'Ran a successful import/export venture? Guide someone sourcing products overseas.' },
                  { icon: <Wrench size={22} />, text: '40+ years as a mechanic? Help diagnose that odd car noise — safe to drive or not?' },
                  { icon: <Palette size={22} />, text: 'Mastered woodworking or joinery? Walk through tricky cuts for a home reno.' },
                  { icon: <BookOpen size={22} />, text: 'Built a thriving career? Give honest "is this AI-proof?" advice or career pivots.' },
                  { icon: <MessageSquare size={22} />, text: 'Enjoyed a 50-year loving marriage? Offer relationship insights that actually work.' },
                  { icon: <Fish size={22} />, text: 'Perfected fishing, gardening, photography? Share the shortcuts that save time and money.' },
                ].map((item, i) => (
                  <div key={i} className="flex gap-4 items-start group" data-testid={`teach-item-${i}`}>
                    <div className="w-10 h-10 rounded-xl bg-mate-green/10 flex items-center justify-center text-mate-green shrink-0 group-hover:bg-mate-green group-hover:text-white transition-colors duration-300">
                      {item.icon}
                    </div>
                    <p className="text-gray-700 text-base leading-relaxed pt-1.5" style={{ lineHeight: '1.6' }}>{item.text}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-center">
              <img
                src="https://images.unsplash.com/photo-1544388388-fb642d61b26d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwyfHxzZW5pb3IlMjBtYW4lMjBzbWlsaW5nJTIwb3V0ZG9vcnMlMjBzdW5saWdodCUyMHBvcnRyYWl0fGVufDB8fHx8MTc3MzI5ODA0NHww&ixlib=rb-4.1.0&q=85&w=800"
                alt="Happy retiree enjoying outdoors"
                className="rounded-3xl shadow-xl max-w-full w-full md:w-[480px] object-cover aspect-[4/5]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Why Now Section */}
      <section className="py-20 md:py-32 px-6 md:px-12 bg-mate-sand" data-testid="why-now-section">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-gray-900 mb-6" style={{ letterSpacing: '-0.02em' }}>
            Why Launching Now?
          </h2>
          <p className="text-lg md:text-xl text-gray-600 leading-relaxed mb-8 max-w-3xl mx-auto" style={{ lineHeight: '1.65' }}>
            Young Aussies face massive changes: AI job shifts, easier entrepreneurship, but huge barriers to knowing "where to start." They need trusted, human advice — not just Google or ChatGPT.
          </p>
          <p className="text-lg md:text-xl text-gray-600 leading-relaxed mb-10 max-w-3xl mx-auto" style={{ lineHeight: '1.65' }}>
            Retirees are perfectly positioned to fill that gap... and get fairly paid for it.
          </p>

          <div className="bg-white rounded-2xl border border-mate-mist p-8 md:p-10 max-w-2xl mx-auto">
            <div className="w-16 h-16 bg-mate-gold/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Users size={32} className="text-mate-gold" />
            </div>
            <h3 className="font-heading font-semibold text-xl text-gray-900 mb-2">Be among the first</h3>
            <p className="text-gray-600 text-base leading-relaxed" style={{ lineHeight: '1.6' }}>
              Early mentors get better visibility, priority matching, and a head start building their profile and reputation.
            </p>
          </div>
        </div>
      </section>

      {/* Final CTA / Signup */}
      <section id="signup-section" className="py-20 md:py-32 px-6 md:px-12 bg-mate-terracotta" data-testid="signup-section">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-white mb-4" style={{ letterSpacing: '-0.02em' }}>
            Ready to Share Your Wisdom?
          </h2>
          <p className="text-lg text-white/85 mb-4">
            No formal qualifications needed. Open to everyone with real experience — especially retirees.
          </p>
          <p className="text-base text-white/70 mb-10">
            We'll email you the moment the platform goes live (about 1 month away). No commitment — just priority access.
          </p>

          {submitted ? (
            <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-8 max-w-md mx-auto" data-testid="signup-success">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                <Check size={32} className="text-mate-terracotta" />
              </div>
              <h3 className="font-heading font-semibold text-2xl text-white mb-2">You're on the list!</h3>
              <p className="text-white/80">We'll be in touch as soon as we launch. Thank you for joining.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-lg mx-auto" data-testid="signup-form">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email address"
                className="flex-1 px-6 py-4 rounded-full text-lg text-gray-900 bg-white placeholder:text-gray-400 outline-none focus:ring-4 focus:ring-white/30 transition-all"
                data-testid="signup-email-input"
                aria-label="Email address"
              />
              <button
                type="submit"
                disabled={loading}
                className="flex items-center justify-center gap-2 bg-mate-green text-white font-semibold text-lg px-8 py-4 rounded-full hover:scale-105 transition-transform duration-200 disabled:opacity-60 disabled:hover:scale-100 whitespace-nowrap"
                data-testid="signup-submit-btn"
                aria-label="Sign up for launch alerts"
              >
                {loading ? 'Joining...' : 'Get Notified'}
                {!loading && <ArrowRight size={20} />}
              </button>
            </form>
          )}
          {error && <p className="text-white/90 mt-4 text-sm" data-testid="signup-error">{error}</p>}
          {waitlistCount > 0 && (
            <p className="mt-6 text-white/80 text-sm flex items-center justify-center gap-2" data-testid="social-proof-counter">
              <Users size={16} />
              Join {waitlistCount} {waitlistCount === 1 ? 'other' : 'others'} already signed up
            </p>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 md:px-12 bg-gray-900" data-testid="landing-footer">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-mate-green flex items-center justify-center">
              <span className="text-white font-heading font-bold text-base">6</span>
            </div>
            <span className="font-heading font-bold text-lg text-white tracking-tight">6th Mate</span>
          </div>
          <p className="text-gray-400 text-sm text-center">
            Questions? Email us at <a href="mailto:support@6thmate.com.au" className="text-mate-terracotta hover:underline">support@6thmate.com.au</a>
          </p>
          <p className="text-gray-500 text-sm">&copy; 2026 6th Mate. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
